import { Injectable, inject, signal } from '@angular/core';
import {
  DRIVE_AUDIENCE_SOURCE,
  DRIVE_SURFACE_DATA_SOURCE,
  type DirectoryNode,
  type DriveAudience,
  type FileAliasNode,
  type ShareLinkCreateOptions,
  type UpdateShareLinkConversationsInput,
  type UpdateShareLinkSettingsInput,
  surfaceGrantKey,
} from '@mtm/drive/core';
import type {
  ShareAudienceEntry,
  ShareConversationFeed,
  ShareDialogAsset,
  ShareDialogResult,
} from '@mtm/drive/components';
import { firstValueFrom, type Subscription } from 'rxjs';

export type ShareTarget = { kind: 'file'; file: FileAliasNode } | { kind: 'directory'; directory: DirectoryNode };

/** Actions the drive surface registers so top-nav can drive the embedded explorer's Share flow. */
export interface DriveShareController {
  createShareLinkForFile(
    file: FileAliasNode,
    title: string,
    options: ShareLinkCreateOptions
  ): Promise<{ surface: DirectoryNode }>;
  createShareLinkForDirectory(
    directory: DirectoryNode,
    title: string,
    options: ShareLinkCreateOptions
  ): Promise<{ surface: DirectoryNode }>;
  renameDirectory(directoryUuid: string, name: string): Promise<void>;
}

/** A link this open of the dialog already created, so a retry finishes it instead of creating another. */
interface CreatedShareLink {
  directoryUuid: string;
  title: string;
  /** The settings the link holds — sent with the create, or patched since — serialised. */
  appliedSettings: string;
  /** Grants that already landed, keyed by `surfaceGrantKey`, so a retry can revoke whoever was taken off the list. */
  granted: Map<string, ShareAudienceEntry>;
  /** Account uuids promoted to co-owner so far. */
  owners: Set<string>;
  /** The conversation set the link holds, serialised; null until one is sent. */
  appliedConversations: string | null;
}

/**
 * The link-level settings a dialog result asks for, in full: a sparse patch cannot
 * express "no longer expires" (`expiryAt: null` means unchanged), which is why
 * `clearExpiry` exists.
 */
function shareLinkSettingsOf(result: ShareDialogResult): UpdateShareLinkSettingsInput {
  const common = {
    permissions: { comment: result.comment, download: result.download },
    isPublic: result.isPublic,
    accessRequestsEnabled: result.accessRequests,
  };
  return result.expiryAt ? { ...common, expiryAt: result.expiryAt } : { ...common, clearExpiry: true };
}

/** The conversations the link's recipients comment in. The dialog's feed ids are audience uuids. */
function shareLinkConversationsOf(result: ShareDialogResult): UpdateShareLinkConversationsInput {
  return { own: result.conversations.newConversation, audienceUuids: result.conversations.feedIds };
}

/** How the dialog's Conversations menu draws each audience kind; a share link's own and a Just me are not feeds. */
const FEED_KINDS: Readonly<Partial<Record<DriveAudience['audienceType'], ShareConversationFeed['kind']>>> = {
  PUBLIC: 'public',
  INTERNAL: 'internal',
  GROUP: 'group',
  DIRECT: 'direct',
};

function toConversationFeed(audience: DriveAudience): ShareConversationFeed[] {
  const kind = FEED_KINDS[audience.audienceType];
  return kind === undefined ? [] : [{ id: audience.audienceUuid, name: audience.name, kind }];
}

/**
 * Mirror of the drive explorer's current share target (previewed file, or the
 * open folder when nothing is previewed) for out-of-tree UI (a top-nav Share
 * button + popover dialog). The explorer owns the underlying data — the drive
 * surface writes `shareTarget`/`shareAsset` via `syncState`; the actual create
 * call is delegated to the registered controller. Mirrors `DriveCompareUiService`.
 */
@Injectable({ providedIn: 'root' })
export class DriveShareUiService {
  private readonly surfaces = inject(DRIVE_SURFACE_DATA_SOURCE, { optional: true });
  private readonly audiences = inject(DRIVE_AUDIENCE_SOURCE, { optional: true });
  private readonly _shareTarget = signal<ShareTarget | null>(null);
  private readonly _shareAsset = signal<ShareDialogAsset | null>(null);
  private readonly _shareOpen = signal(false);
  private readonly _shareError = signal<string | null>(null);
  private readonly _conversationFeeds = signal<ReadonlyArray<ShareConversationFeed>>([]);
  private _controller: DriveShareController | null = null;
  private _feedsRead: Subscription | null = null;

  readonly shareTarget = this._shareTarget.asReadonly();
  readonly shareAsset = this._shareAsset.asReadonly();
  readonly shareOpen = this._shareOpen.asReadonly();
  readonly shareError = this._shareError.asReadonly();
  /** The caller's postable audiences, offered by the dialog's Conversations menu. */
  readonly conversationFeeds = this._conversationFeeds.asReadonly();

  public registerController(controller: DriveShareController): void {
    this._controller = controller;
  }

  /** Identity-checked so a late destroy never clobbers a newer surface's registration. */
  public unregisterController(controller: DriveShareController): void {
    if (this._controller === controller) {
      this._controller = null;
      this._shareTarget.set(null);
      this._shareAsset.set(null);
      this._shareError.set(null);
      // Also drops a half-finished link, so a share from the next surface creates
      // its own link instead of re-applying onto this one.
      this.close();
    }
  }

  /** Called only by the drive surface's mirror effect. */
  public syncState(target: ShareTarget | null, asset: ShareDialogAsset | null): void {
    this._shareTarget.set(target);
    this._shareAsset.set(asset);
  }

  /**
   * The target the dialog is currently open for, captured at open time. Using
   * this (rather than re-reading `shareTarget()` on confirm) protects against
   * the explorer's own state shifting while the popover is open — e.g.
   * `inSurfaceContext()`/`selectedFile()` changing transiently and re-running
   * the drive surface's sync effect — which would otherwise null out the
   * target mid-flow and make `confirm()` silently no-op.
   */
  private _openedTarget: ShareTarget | null = null;
  private _created: CreatedShareLink | null = null;

  public toggleOpen(): void {
    if (this._shareOpen()) {
      this.close();
    } else {
      this._shareError.set(null);
      this._openedTarget = this._shareTarget();
      this._shareOpen.set(true);
      this.readConversationFeeds();
    }
  }

  public close(): void {
    this._shareOpen.set(false);
    this._openedTarget = null;
    this._created = null;
  }

  /**
   * Re-read on every open: audiences have no push events, and one created from the
   * composer since the last open should be offered. A failure leaves the menu with
   * the link's own conversation alone.
   */
  private readConversationFeeds(): void {
    if (!this.audiences) {
      return;
    }
    this._feedsRead?.unsubscribe();
    this._feedsRead = this.audiences.listPostable().subscribe({
      next: (rows) => this._conversationFeeds.set(rows.flatMap(toConversationFeed)),
      error: () => this._conversationFeeds.set([]),
    });
  }

  /**
   * Creates the link with its permissions, public flag, expiry and access-requests
   * switch, so it is never open with settings it was not asked for, then applies the
   * rest of the dialog's result — the audience, co-owners and conversations. The
   * explorer's create owns the sidebar index, so the create goes through it.
   *
   * If a follow-up step fails the link already exists: the dialog stays open, and
   * a retry renames/re-applies onto that same link instead of creating a duplicate.
   */
  public async confirm(result: ShareDialogResult): Promise<void> {
    const target = this._openedTarget ?? this._shareTarget();
    const controller = this._controller;
    if (!target || !controller) {
      this._shareError.set('drive_shareCreateFailed');
      return;
    }
    this._shareError.set(null);

    let created = this._created;
    try {
      if (!created) {
        const settings = shareLinkSettingsOf(result);
        const options: ShareLinkCreateOptions = {
          permissions: settings.permissions,
          isPublic: settings.isPublic,
          expiryAt: settings.expiryAt ?? null,
          accessRequestsEnabled: settings.accessRequestsEnabled,
        };
        const { surface } = await (target.kind === 'file'
          ? controller.createShareLinkForFile(target.file, result.title, options)
          : controller.createShareLinkForDirectory(target.directory, result.title, options));
        created = {
          directoryUuid: surface.directoryUuid,
          title: result.title,
          appliedSettings: JSON.stringify(settings),
          granted: new Map(),
          owners: new Set(),
          appliedConversations: null,
        };
        this._created = created;
      } else if (created.title !== result.title) {
        await controller.renameDirectory(created.directoryUuid, result.title);
        created.title = result.title;
      }
    } catch {
      this._shareError.set(created ? 'drive_shareSettingsFailed' : 'drive_shareCreateFailed');
      return;
    }

    try {
      await this.applySettings(created, result);
      this.close();
    } catch {
      this._shareError.set('drive_shareSettingsFailed');
    }
  }

  /**
   * Brings the created link in line with what the dialog currently says. On a retry
   * the user may have edited the form, so this reconciles rather than replays: each
   * step compares what already landed against the dialog and sends only what differs.
   * Nothing is emailed — v2 invites send no mail.
   */
  private async applySettings(created: CreatedShareLink, result: ShareDialogResult): Promise<void> {
    const surfaces = this.surfaces;
    if (!surfaces) {
      throw new Error('DRIVE_SURFACE_DATA_SOURCE is not provided.');
    }
    const { directoryUuid, granted } = created;

    // The create carried the settings; re-sent in full only when a retry changed them.
    const settings = shareLinkSettingsOf(result);
    const settingsPayload = JSON.stringify(settings);
    if (created.appliedSettings !== settingsPayload) {
      await firstValueFrom(surfaces.updateShareLinkSettings(directoryUuid, settings));
      created.appliedSettings = settingsPayload;
    }

    const wanted = new Map(result.audience.map((entry) => [surfaceGrantKey(entry.grantType, entry.value), entry]));
    // Each grant records itself the moment it lands, so a partial failure leaves an
    // accurate picture for the next retry. Revoking has no link-level endpoint.
    const revokes = [...granted]
      .filter(([key]) => !wanted.has(key))
      .map(([key, entry]) =>
        firstValueFrom(surfaces.revokeAccess(directoryUuid, entry.grantType, entry.value)).then(() => {
          granted.delete(key);
        })
      );
    const invites = [...wanted]
      .filter(([key]) => !granted.has(key))
      .map(([key, entry]) =>
        firstValueFrom(
          surfaces.inviteToShareLink(directoryUuid, { grantType: entry.grantType, grantValue: entry.value })
        ).then(() => {
          granted.set(key, entry);
        })
      );

    // Wait for every grant, not just the first rejection: one still in flight must
    // reach `granted` before a retry is possible, or that retry would miss revoking
    // it. (Hand-rolled allSettled — the app's `lib` predates ES2020.)
    const failures: unknown[] = [];
    await Promise.all(
      [...revokes, ...invites].map((step) =>
        step.then(
          () => undefined,
          (error: unknown) => {
            failures.push(error);
          }
        )
      )
    );
    if (failures.length > 0) {
      throw failures[0];
    }

    // After the invitations: an owner is also a recipient, promoted on top of the
    // grant that lets them open the link.
    const owners = new Set(result.owners.map((owner) => owner.accountUuid));
    const add = [...owners].filter((uuid) => !created.owners.has(uuid));
    const remove = [...created.owners].filter((uuid) => !owners.has(uuid));
    if (add.length > 0 || remove.length > 0) {
      await firstValueFrom(surfaces.updateShareLinkOwners(directoryUuid, { add, remove }));
      created.owners = owners;
    }

    // Last: the backend grafts the link's recipients onto each ticked group, so the
    // invitations above decide who gets grafted. Replaces the set, so no diff is needed.
    const conversations = shareLinkConversationsOf(result);
    const conversationsPayload = JSON.stringify(conversations);
    if (created.appliedConversations !== conversationsPayload) {
      await firstValueFrom(surfaces.updateShareLinkConversations(directoryUuid, conversations));
      created.appliedConversations = conversationsPayload;
    }
  }
}
